import { CAMPAIGN, TemplateType } from 'constants/templateTypes';
import { isString } from 'lodash';
import { AudienceList } from 'modules/domains/campaign';
import { FolderId } from 'modules/domains/foldering';
import { getRaw, postFormRaw, postRaw } from 'services/api';
import { getXsrfToken } from 'utils/cookies';
import { Nullable } from 'utils/tsHelpers';

// new endpoint that gets template as a json blob.
export const TEMPLATE_DATA_BY_ID_URL = '/templates/templateData.json?';
export const CREATE_TEMPLATE_BASE_URL = '/templates/create';
export const CREATE_TEMPLATE_URL = '/templates/create?messageMedium=';
export const CREATE_BASE_BEE_TEMPLATE_URL = '/templates/builder-templates/new';
export const CREATE_BEE_TEMPLATE_URL = '/templates/builder-templates/new?messageMedium=';
export const IMPORT_TEMPLATE_URL = '/templates/importSubmit';
export const TEMPLATES_URL = '/templates/data.json?';
export const CLONE_TEMPLATE_URL = '/templates/clone?';
export const TEMPLATE_SENDERS_URL = '/templates/senders.json';

// campaign picker urls
export const COPY_TEMPLATE_TO_CAMPAIGN_URL = '/templates/copyTemplateToCampaign';
export const CAMPAIGN_TYPE_TEMPLATES_URL = '/templates/campaigns?';

export const createTemplate = (
  campaignId: number | undefined,
  messageMedium: string,
  destinationFolderId?: FolderId,
  defaultLocale?: string,
  templateType?: TemplateType,
  placementId?: number,
) => {
  const params = new URLSearchParams('');
  params.set('messageMedium', messageMedium);
  if (destinationFolderId) {
    params.set('destinationFolderId', destinationFolderId.toString());
  }
  if (campaignId) {
    params.set('campaignId', campaignId.toString());
  }

  if (defaultLocale) {
    params.set('defaultLocale', defaultLocale);
  }
  if (templateType) {
    params.set('templateType', templateType);
  }
  if (placementId) {
    params.set('placementId', placementId.toString());
  }

  return getRaw(`${CREATE_TEMPLATE_BASE_URL}?${params.toString()}`);
};

export const createBeeTemplate = (
  campaignId: number | undefined,
  messageMedium: string,
  slug: string,
  destinationFolderId: number | null,
) => {
  const params = new URLSearchParams('');
  params.set('messageMedium', messageMedium);
  params.set('slug', slug);
  if (destinationFolderId) {
    params.set('destinationFolderId', destinationFolderId.toString());
  }
  if (campaignId) {
    params.set('campaignId', campaignId.toString());
  }

  return getRaw(`${CREATE_BASE_BEE_TEMPLATE_URL}?${params.toString()}`);
};

export const importTemplate = (campaignId: number | undefined, template: File | string) => {
  const bodyFormData = new FormData();

  // the template submitted may either be a url string
  // at which point the backend creates a template based off
  // the html found at the url
  if (isString(template)) {
    bodyFormData.append('url', template);
  } else {
    // @ts-ignore
    bodyFormData.append('templateFile', template);

    // or its an actual html file provided by the user.
    const xsrfToken = getXsrfToken();
    if (xsrfToken) {
      bodyFormData.append('csrfToken', xsrfToken); // xsrf and csrf are the same token.
    }
  }

  const endpoint = !campaignId
    ? IMPORT_TEMPLATE_URL
    : `${IMPORT_TEMPLATE_URL}?campaignId=${campaignId}`;

  // @ts-ignore
  return postFormRaw(endpoint, bodyFormData);
};

export type TemplateForSelection = {
  id: number;
  name: string;
  url: string;
  subject: string;
  messageMedium: string;
  createdAt: string;
  createdAtRaw: number;
  updatedAt: string;
  updatedAtRaw: number;
  updatedBy: string;
  hideRow: boolean;
  highlightRow: boolean;
  locales: any[];
  templateType: string;
  campaignId: Nullable<number>;
  campaignName: Nullable<string>;
  campaignState: Nullable<string>;
  workflowName: Nullable<string>;
  workflowId: Nullable<number>;
  experimentName: Nullable<string>;
  experimentId: Nullable<number>;
  sendLists: AudienceList[];
  suppressionLists: AudienceList[];
};

export type GetTemplatesResponse = {
  total: number;
  results: TemplateForSelection[];
};

export const getTemplates = (
  messageMedium: string,
  orderBy: string,
  page: number,
  pageSize: number,
  sorting: string,
  templateType: string,
  filter?: string,
  placementId?: number,
  whatsAppTemplateType?: TemplateType,
) => {
  /**
   * There is a separate backend API for `CAMPAIGN` templates as opposed to every other type of template.
   * The information returned from both endpoints has the exact same data structure, except the type of templates returns differs.
   * `CAMPAIGN` templates are actually an amalgam of `BLAST` and `TRIGGERED` templateTypes.
   * So if we want to get `BASE` or `WORKFLOW` templates (or `BLAST`, or `TRIGGERED` individually), we'd use `TEMPLATES_URL` route.
   * If we want `CAMPAIGN` templates instead, we'd use `CAMPAIGN_TYPE_TEMPLATES_URL`.
   */
  const baseUrl = templateType === CAMPAIGN ? CAMPAIGN_TYPE_TEMPLATES_URL : TEMPLATES_URL;
  const queryParams = [
    `messageMedium=${messageMedium}`,
    `orderBy=${orderBy}`,
    `page=${page}`,
    `pageSize=${pageSize}`,
    `sorting=${sorting}`,
    `templateType=${templateType}`,
    ...(whatsAppTemplateType ? [`whatsAppTemplateType=${whatsAppTemplateType}`] : []),
    ...(filter ? [`filter=${filter}`] : []),
    ...(placementId ? [`placementId=${placementId}`] : []),
  ];
  const fullUrl = baseUrl + queryParams.join('&');

  return getRaw<GetTemplatesResponse>(fullUrl);
};

export const cloneTemplate = (templateId: number, campaignId?: number) => {
  const url = `${CLONE_TEMPLATE_URL}templateId=${templateId}${
    campaignId ? `&campaignId=${campaignId}` : ''
  }`;
  return getRaw(url);
};

export const getTemplateDataById = (id: string, locale: string | undefined) => {
  const url = `${TEMPLATE_DATA_BY_ID_URL}templateId=${id}${locale ? `&locale=${locale}` : ''}`;
  return getRaw(url);
};

export const getSenders = () => getRaw(TEMPLATE_SENDERS_URL);

export const copyTemplateToCampaign = (
  campaignId: number,
  templateId: number,
  dataFeedId?: number,
) =>
  postRaw(COPY_TEMPLATE_TO_CAMPAIGN_URL, {
    campaignId,
    templateId,
    dataFeedId,
  });
